import { Box, Grid, Paper, Typography } from '@mui/material'
import React, { useMemo } from 'react'
import NumericLabel from 'react-pretty-numbers'
import NoDataAvailableCard from './NoDataAvailableCard'
import GraphCard from './GraphCard'
import StatTable from './StatTable'
import CardSkeleton from './CardSkeleton'
import CardFilter from './CardFilter'

const APPROVAL_TURNAROUND_COLUMNS = [
  { field: 'pending', label: 'Pending' },
  { field: 'label', label: 'Oldest' },
  { field: 'submitted', label: 'Approved YTD' },
]

const SHOP_VISIT_STATUS_STYLES = {
  1: { bg: '#f0edff', color: '#534792' },
  2: { bg: '#e6edff', color: '#3659a7' },
  3: { bg: '#dcf5e8', color: '#18754e' },
  4: { bg: '#fde4e4', color: '#ae2828' },
  7: { bg: '#fff0d8', color: '#a46113' },
}


const SHOP_VISITS_COLUMNS = [
  { field: 'event', label: 'Event' },
  { field: 'date', label: 'Date' },
  {
    field: 'cost',
    label: 'Cost',
    render: (row) => row?.cost == null ? '--' : (
      <NumericLabel params={{ locales: 'en-GB', currency: true, currencyIndicator: row?.currency?.code, precision: 0, commafy: true, justification: 'L' }}>
        {row.cost}
      </NumericLabel>
    ),
  },
  {
    field: 'status',
    label: 'Status',
    render: (row) => {
      if (!row?.status?.label) return '--'
      const style = SHOP_VISIT_STATUS_STYLES[row.status.value] || { bg: '#e6edff', color: '#3659a7' }
      return (
        <Box
          className="status-btn"
          sx={{
            background: style.bg,
            color: `${style.color} !important`,
            display: 'inline-flex',
            alignItems: 'center',
            width: 'fit-content',
            whiteSpace: 'nowrap',
            fontSize: '11px',
            fontWeight: 700,
            px: 1,
            borderRadius: '10px',
          }}
        >
          {row.status.label}
        </Box>
      )
    },
  },
]
const ClaimsAndShopVisits = ({ loading = {}, claimsData, filters = {}, onFilterChange, currencies = [], cardFiltersDisabled = false }) => {
  const claimStatusOptions = claimsData?.claim_status_recovery?.graph_values?.options
  const claimStatusGraphOption = useMemo(() => {
    if (!claimStatusOptions) return null
    return {
      ...claimStatusOptions,
      title: undefined,
      series: claimStatusOptions.series?.map((s) => ({
        ...s,
        center: ['35%', '50%'],
      })),
    }
  }, [claimStatusOptions])

  return (
    <div className="contract-analytics-wrapper">
      <div className="ca-page-header">
        <Typography fontSize='15px' fontFamily="Roboto-SemiBold !important">Claims & Shop Visits </Typography>
      </div>
      <Grid container spacing={1.5} mx={1} sx={{ alignItems: 'stretch' }}>
        <Grid size={{ md: 4, xs: 12 }} sx={{ display: 'flex' }}>
          <Paper className="ca-equal-card" >
            <CardFilter disabled={cardFiltersDisabled} title="Claim Status & Recovery" filter={filters.claim_status_recovery} onChange={(filter) => onFilterChange('claim_status_recovery', filter)} currencyOptions={currencies} />
            {loading.claim_status_recovery ? (
              <CardSkeleton />
            ) : (
              <div className="ca-graph-container">
                {!claimStatusGraphOption ? (
                  <NoDataAvailableCard title='No Claim Status & Recovery data available' subtext='Try adjusting the date range' />
                ) : (
                  <GraphCard option={claimStatusGraphOption} height='225px' />
                )}
              </div>
            )}
          </Paper>
        </Grid>
        <Grid size={{ md: 4, xs: 12 }} sx={{ display: 'flex' }}>
          <Paper className="ca-equal-card ca-side-card">
            <CardFilter disabled={cardFiltersDisabled} title="Claim Approval Turnaround" filter={filters.claim_approval_turnaround} onChange={(filter) => onFilterChange('claim_approval_turnaround', filter)} />
            {loading.claim_approval_turnaround ? (
              <CardSkeleton />
            ) : (
              <div className="ca-claim-block ca-card-body">
                {!claimsData?.claim_approval_turnaround?.graph_values?.monthly_trend?.length ? (
                  <NoDataAvailableCard title='No Claim Approval Turnaround data available' subtext='Try adjusting the date range' />
                ) : (
                  <div className='clm-aprv-trnard'>
                    <Typography variant='h5' fontWeight={900}>
                      {claimsData?.claim_approval_turnaround?.graph_values?.avg_days_to_approve != null
                        ? `${claimsData.claim_approval_turnaround.graph_values.avg_days_to_approve} days`
                        : '--'}
                    </Typography>
                    <span className="ca-claim-subtext">Average submission-to-approval</span>
                    <div className='ca-scroll-table'>
                      <StatTable columns={APPROVAL_TURNAROUND_COLUMNS} rows={claimsData?.claim_approval_turnaround?.graph_values?.monthly_trend || []} />
                    </div>
                  </div>
                )}
              </div>
            )}
          </Paper>
        </Grid>
        <Grid size={{ md: 4, xs: 12 }} sx={{ display: 'flex' }}>
          <Paper className="ca-equal-card">
            <CardFilter disabled={cardFiltersDisabled} title="Planned / Completed Shop Visits" filter={filters.planned_shop_visits} onChange={(filter) => onFilterChange('planned_shop_visits', filter)} currencyOptions={currencies} />
            {loading.planned_shop_visits ? (
              <CardSkeleton />
            ) : (
              <div className="ca-claim-block ca-card-body">
                {!claimsData?.planned_shop_visits?.graph_values?.rows?.length ? (
                  <NoDataAvailableCard title='No  Planned / Completed Shop Visits data available' subtext='Try adjusting the date range' />
                ) : (
                  <div className='clm-aprv-trnard ca-scroll-table'>
                    <StatTable columns={SHOP_VISITS_COLUMNS} rows={claimsData?.planned_shop_visits?.graph_values?.rows} />
                  </div>
                )}
              </div>
            )}
          </Paper>
        </Grid>
      </Grid>
    </div>
  )
}

export default ClaimsAndShopVisits
